import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useHistory, useLocation } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/GetApp';
import { DataAPIContext, GET_LIST } from '../api';
import { ContextStore } from '../store/contextStore';
import TourMediaCard from './TourMediaCard';
import TourMediaListToolbar from './TourMediaListToolbar';
import FixedLinearProgress from '../components/FixedLinearProgress';
import AccessDeniedView from '../components/AccessDeniedView';
import OutlinedExportButton from '../components/OutlinedExportButton';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import config from '../config';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: `calc(${theme.cp.contentHeight} - 3rem)`,
        paddingBottom: '1rem',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: '#FCFCFE',
        [theme.breakpoints.down('md')]: {
            padding: '16px 16px 12px',
        },
    },
    actionsBox: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'nowrap',
        flexShrink: 0,
        [theme.breakpoints.down('md')]: {
            gap: '4px',
        },
    },
    contentWrapper: {
        flex: 1,
        padding: '0 16px',
        display: 'flex',
        flexDirection: 'column',
    },
    gridContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        padding: '0 16px 16px 16px',
        border: '1px solid #D8D8D8',
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        alignContent: 'flex-start',
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
    },
}));

const TourMediaListPanel = () => {
    const classes = useStyles();
    const theme = useTheme();
    const compactHeader = useMediaQuery(theme.breakpoints.down('md'));
    const history = useHistory();
    const location = useLocation();
    const dataAPI = useContext(DataAPIContext);
    const { token, hasPerm } = useContext(ContextStore);

    const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());
    const toIsoString = (date) => (isValidDate(date) ? date.toISOString() : null);
    const toApiDate = (date) => (isValidDate(date) ? date.toISOString().split('T')[0] : null);
    const parseSavedDate = (value) => {
        if (!value) return null;
        const parsed = new Date(value);
        return isValidDate(parsed) ? parsed : null;
    };

    const [filters, setFilters] = useState(() => {
        const savedState = sessionStorage.getItem('tourMediaListState');
        if (savedState) {
            try {
                const parsed = JSON.parse(savedState);
                return {
                    quick_filter: parsed.filters.quick_filter || 'all_time',
                    start_date: parseSavedDate(parsed.filters.start_date),
                    end_date: parseSavedDate(parsed.filters.end_date),
                    tour_id: parsed.filters.tour_id || null,
                    tour_option: parsed.filters.tour_option || null,
                };
            } catch (e) {
                console.error('Failed to parse saved state:', e);
            }
        }
        return {
            quick_filter: 'last_90_days',
            start_date: null,
            end_date: null,
            tour_id: null,
            tour_option: null,
        };
    });

    const [tours, setTours] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [selectedTours, setSelectedTours] = useState([]);
    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
    const loadMoreRef = useRef(null);
    const [showNoMore, setShowNoMore] = useState(false);
    const noMoreTimeoutRef = useRef(null);
    const hasScrolledRef = useRef(false);
    const selectedMediaCount = useMemo(
        () => selectedTours.reduce((sum, id) => {
            const tour = tours.find(t => t.id === id);
            return sum + (tour?.media_count || 0);
        }, 0),
        [selectedTours, tours]
    );

    const downloadLabel = downloading
        ? 'downloading...'
        : `download files${selectedMediaCount || selectedMediaCount === 0 ? ` (${selectedMediaCount})` : ''}`;

    useEffect(() => {
        sessionStorage.setItem('tourMediaListState', JSON.stringify({
            filters: {
                quick_filter: filters.quick_filter,
                start_date: toIsoString(filters.start_date),
                end_date: toIsoString(filters.end_date),
                tour_id: filters.tour_id,
                tour_option: filters.tour_option,
            },
        }));

        setTours([]);
        setPage(1);
        setHasMore(true);
        setSelectedTours([]);
        setLastSelectedIndex(null);
        hasScrolledRef.current = false;
        fetchTours(1, true);
    }, [filters]);

    useEffect(() => {
        const handleScroll = () => {
            hasScrolledRef.current = true;
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    if (!loading && hasMore) {
                        fetchTours(page + 1, false);
                    } else if (!hasMore && tours.length > 0 && hasScrolledRef.current) {
                        if (noMoreTimeoutRef.current) {
                            clearTimeout(noMoreTimeoutRef.current);
                        }
                        setShowNoMore(true);
                        noMoreTimeoutRef.current = setTimeout(() => {
                            setShowNoMore(false);
                        }, 3000);
                    }
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
            if (noMoreTimeoutRef.current) {
                clearTimeout(noMoreTimeoutRef.current);
            }
        };
    }, [loading, hasMore, page, tours.length]);

    useEffect(() => {
        return () => {
            const currentPath = window.location.pathname;
            if (!currentPath.startsWith('/marketing-suite/media/')) {
                sessionStorage.removeItem('tourMediaListState');
            }
        };
    }, []);

    const fetchTours = async (pageToLoad = page, reset = false) => {
        if (!reset && !hasMore) return;
        
        setLoading(true);
        setError(null);
        try {
            const filterParams = {};

            if (filters.quick_filter && filters.quick_filter !== 'all_time') {
                filterParams.quick_filter = filters.quick_filter;
            }
            const startDate = toApiDate(filters.start_date);
            const endDate = toApiDate(filters.end_date);
            if (startDate) {
                filterParams.start_date = startDate;
            }
            if (endDate) {
                filterParams.end_date = endDate;
            }
            if (filters.tour_id) {
                filterParams.tour_id = filters.tour_id;
            }

            const response = await dataAPI(GET_LIST, 'tour/media', {
                pagination: { page: pageToLoad, perPage: 25 },
                sort: { field: 'first_date', order: 'DESC' },
                filter: filterParams,
            });

            if (reset) {
                setTours(response.data || []);
            } else {
                setTours(prev => [...prev, ...(response.data || [])]);
            }
            setHasMore((response.data || []).length === 25);
            setPage(pageToLoad);
        } catch (error) {
            console.error('Error fetching tours:', error);
            setError('Failed to load tours. Please try again.');
            if (reset) {
                setTours([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTourSelect = useCallback((tourId, index, event) => {
        if (event?.shiftKey && lastSelectedIndex !== null) {
            const start = Math.min(lastSelectedIndex, index);
            const end = Math.max(lastSelectedIndex, index);
            const rangeIds = tours.slice(start, end + 1).map(t => t.id);
            
            setSelectedTours((prev) => {
                const newSelected = new Set(prev);
                rangeIds.forEach(id => newSelected.add(id));
                return Array.from(newSelected);
            });
        } else {
            setSelectedTours((prev) =>
                prev.includes(tourId)
                    ? prev.filter((id) => id !== tourId)
                    : [...prev, tourId]
            );
        }
        setLastSelectedIndex(index);
    }, [lastSelectedIndex, tours]);

    const handleSelectAll = () => {
        setSelectedTours(tours.map(t => t.id));
    };

    const handleSelectNone = () => {
        setSelectedTours([]);
    };

    const handleTourClick = useCallback((tourId) => {
        if (!hasPerm('tour_media', 'read')) {
            return;
        }
        if (!selectedTours.includes(tourId)) {
            history.push(`/marketing-suite/media/${tourId}`);
        }
    }, [selectedTours, history, hasPerm]);

    const handleDownloadSelected = async () => {
        if (downloading) return;
        
        setDownloading(true);
        const tourIds = selectedTours.join(',');
        const url = `${config.apiUrl}/tour/media/download/?tour_ids=${tourIds}`;
        
        try {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Token ${token}`,
                },
            });
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `tour_media_${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading ZIP:', error);
        } finally {
            setDownloading(false);
        }
    };

    const hasActiveFilters = () => {
        return (
            selectedTours.length > 0 ||
            (filters.quick_filter && filters.quick_filter !== 'all_time') ||
            filters.start_date ||
            filters.end_date ||
            filters.tour_id
        );
    };

    const handleExportClick = () => {
        if (!hasActiveFilters()) {
            setExportConfirmOpen(true);
        } else {
            performExport();
        }
    };

    const performExport = async () => {
        setExportConfirmOpen(false);
        try {
            setLoading(true);
            const filterParams = {};

            if (selectedTours.length > 0) {
                filterParams.tour_ids = selectedTours.join(',');
            } else {
                if (filters.quick_filter && filters.quick_filter !== 'all_time') {
                    filterParams.quick_filter = filters.quick_filter;
                }
                const startDate = toApiDate(filters.start_date);
                const endDate = toApiDate(filters.end_date);
                if (startDate) {
                    filterParams.start_date = startDate;
                }
                if (endDate) {
                    filterParams.end_date = endDate;
                }
                if (filters.tour_id) {
                    filterParams.tour_id = filters.tour_id;
                }
            }

            const params = new URLSearchParams(filterParams);
            const url = `${config.apiUrl}/tour/media/export/?${params.toString()}`;
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Token ${token}`,
                },
            });
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `tour_media_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error exporting list:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className={classes.root}>
            <PageMeta title="Marketing Suite" />
            <FixedLinearProgress visibility={loading && tours.length === 0} />
            <Box className={classes.header}>
                <PageHeading sx={{ whiteSpace: 'nowrap' }}>Marketing Suite</PageHeading>
                <Box className={classes.actionsBox}>
                    {hasPerm('tour_media', 'read') && (
                        <OutlinedExportButton
                            onClick={handleExportClick}
                            tooltip="Export list of media files as CSV"
                            hideLabel={compactHeader}
                        />
                    )}
                    {selectedTours.length < tours.length && tours.length > 0 && (
                        <Button
                            variant="text"
                            onClick={handleSelectAll}
                            sx={{
                                textTransform: 'uppercase',
                                color: '#007AFF',
                                fontSize: '14px',
                                fontWeight: 500,
                                letterSpacing: '0.07em',
                                [theme.breakpoints.down('md')]: {
                                    fontSize: '12px',
                                    padding: '4px 8px',
                                    minWidth: 'auto',
                                },
                            }}
                        >
                            select all
                        </Button>
                    )}
                    {selectedTours.length > 0 && (
                        <Button
                            variant="text"
                            onClick={handleSelectNone}
                            sx={{
                                textTransform: 'uppercase',
                                color: '#007AFF',
                                fontSize: '14px',
                                fontWeight: 500,
                                letterSpacing: '0.07em',
                                [theme.breakpoints.down('md')]: {
                                    fontSize: '12px',
                                    padding: '4px 8px',
                                    minWidth: 'auto',
                                },
                            }}
                        >
                            select none
                        </Button>
                    )}
                    {selectedTours.length > 0 && hasPerm('tour_media', 'read') && (
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadSelected}
                            disabled={downloading}
                            sx={{
                                textTransform: 'uppercase',
                                color: '#007AFF',
                                borderColor: '#007AFF',
                                fontSize: '14px',
                                fontWeight: 500,
                                letterSpacing: '0.07em',
                                [theme.breakpoints.down('md')]: {
                                    fontSize: '12px',
                                    padding: '4px 8px',
                                    minWidth: 'auto',
                                },
                            }}
                        >
                            {downloadLabel}
                        </Button>
                    )}
                </Box>
            </Box>

            <Box className={classes.contentWrapper}>
                <TourMediaListToolbar
                    filters={filters}
                    onFiltersChange={setFilters}
                    selectedCount={selectedTours.length}
                    onDownloadSelected={handleDownloadSelected}
                    onExportList={handleExportClick}
                />

                {error ? (
                    <Box className={classes.gridContainer}>
                        <Typography sx={{ fontSize: '14px', color: '#d32f2f', width: '100%', textAlign: 'center', padding: '40px' }}>
                            {error}
                        </Typography>
                    </Box>
                ) : !loading && tours.length === 0 ? (
                    <Box className={classes.gridContainer}>
                        <Typography sx={{ fontSize: '14px', color: '#666', width: '100%', textAlign: 'center', padding: '40px' }}>
                            No media files were found for the selected tour
                        </Typography>
                    </Box>
                ) : tours.length === 0 ? (
                    <Box className={classes.gridContainer} />
                ) : (
                    <Box className={classes.gridContainer}>
                        {tours.map((tour, index) => (
                            <TourMediaCard
                                key={`tour-${tour.id}-${index}`}
                                tour={tour}
                                index={index}
                                selected={selectedTours.includes(tour.id)}
                                onSelect={handleTourSelect}
                                onClick={handleTourClick}
                            />
                        ))}
                    </Box>
                )}

                <Box ref={loadMoreRef} sx={{ height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, mb: 2 }}>
                    {loading && hasMore && tours.length > 0 && (
                        <Typography variant="body2" color="text.secondary">Loading more...</Typography>
                    )}
                    {showNoMore && (
                        <Typography variant="body2" color="text.secondary">No more tours</Typography>
                    )}
                </Box>
            </Box>

            <Dialog
                open={exportConfirmOpen}
                onClose={() => setExportConfirmOpen(false)}
            >
                <DialogTitle>Export All Media</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        No tours selected and no filters applied. This will export a list of all media files from all tours. Do you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={performExport} variant="contained" autoFocus>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const TourMediaList = () => {
    const { hasPerm } = useContext(ContextStore);
    if (!hasPerm('tour_media', 'list')) {
        return (
            <>
                <PageMeta title="Marketing Suite" />
                <AccessDeniedView />
            </>
        );
    }
    return <TourMediaListPanel />;
};

export default TourMediaList;
